import React from 'react';
import { MoreHoriz } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { AttributeIconSize } from '@/components/AttributeIcon';
import FilterParams from '@/constants/constituents/FilterParams';
import ConstituentSearchColumn from '@/constants/ConstituentSearchColumn';
import useMobileView from '@/hooks/ui/useMobileView';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { ConstituentIconTiles } from '@/modules/ConstituentDetails';
import DataList, { type DataListColumn } from '@/modules/DataList';
import type SearchConstituent from '@/types/api/SearchConstituent';
import renderCustomFieldValue from '@/utils/constituents/renderCustomFieldValue';
import bioColumn from '../columns/bioColumn';
import cityColumn from '../columns/cityColumn';
import connectionsColumn from '../columns/connectionsColumn';
import countryColumn from '../columns/countryColumn';
import developersColumn from '../columns/developersColumn';
import dobColumn from '../columns/dobColumn';
import labelsColumn from '../columns/labelsColumn';
import lastInteractionDateColumn from '../columns/lastInteractionDateColumn';
import lifetimeCountColumn from '../columns/lifetimeCountColumn';
import lifetimeDonationsColumn from '../columns/lifetimeDonationsColumn';
import lifetimeHoursColumn from '../columns/lifetimeHoursColumn';
import socialMediaColumn from '../columns/socialMediaColumn';
import stateColumn from '../columns/stateColumn';
import thisYearCountColumn from '../columns/thisYearCountColumn';
import thisYearDonationsColumn from '../columns/thisYearDonationsColumn';
import thisYearHoursColumn from '../columns/thisYearHoursColumn';
import ColumnMaxMinWidthClasses from '../constants/ColumnMaxMinWidthClasses';
import PopoverMenuType from '../constants/PopoverMenuType';
import ConstituentActions from './ConstituentActions';
import ConstituentAvatar from './ConstituentAvatar';
import ConstituentPopover from './ConstituentPopover';

const TABLE_ROW_PROPS = {
  className: 'relative py-3 pl-8 md:static md:py-0 md:pl-0',
  hover: true,
};

const ORDERED_COLUMNS = [
  ConstituentSearchColumn.CONSTITUENT_CATEGORIES,
  ConstituentSearchColumn.CONSTITUENT_RULES,
  ConstituentSearchColumn.DEVELOPERS,
  ConstituentSearchColumn.LAST_INTERACTION_DATE,
  ConstituentSearchColumn.BIO,
  ConstituentSearchColumn.DATE_OF_BIRTH,
  ConstituentSearchColumn.PRIMARY_CITY,
  ConstituentSearchColumn.PRIMARY_STATE,
  ConstituentSearchColumn.PRIMARY_COUNTRY,
  ConstituentSearchColumn.LABELS,
  ConstituentSearchColumn.INSTAGRAM,
  ConstituentSearchColumn.TWITTER,
  ConstituentSearchColumn.LINKEDIN,
  ConstituentSearchColumn.FACEBOOK,
  ConstituentSearchColumn.YOUTUBE,
  ConstituentSearchColumn.TIKTOK,
  ConstituentSearchColumn.LIFETIME_COUNT,
  ConstituentSearchColumn.THIS_YEAR_COUNT,
  ConstituentSearchColumn.LIFETIME_DONATIONS,
  ConstituentSearchColumn.THIS_YEAR_DONATIONS,
  ConstituentSearchColumn.LIFETIME_HOURS,
  ConstituentSearchColumn.THIS_YEAR_HOURS,
  ConstituentSearchColumn.NUM_CONNECTIONS,
];

type ConstituentDataListProps = {
  columns?: ConstituentSearchColumn[];
  customFieldColumns?: string[];
  constituents?: SearchConstituent[];
  isLagging?: boolean;
  excludedItems?: string[];
  selectedItems?: string[];
  setExcludedItems?: (items: string[]) => void;
  setSelectedItems?: (items: string[]) => void;
  totalRows?: number;
};

function ConstituentDataList({
  columns = [],
  customFieldColumns = [],
  constituents = [],
  excludedItems = [],
  isLagging,
  selectedItems = [],
  setExcludedItems = () => {},
  setSelectedItems = () => {},
  totalRows,
}: ConstituentDataListProps) {
  const isMobile = useMobileView();
  const [activeMenu, setActiveMenu] = React.useState<null | {
    target: EventTarget;
    constituent: SearchConstituent;
    type: PopoverMenuType;
  }>(null);

  const { data: customFieldData = [] } = useCustomFields();

  React.useEffect(() => {
    if (
      selectedItems.length === 0 ||
      selectedItems.length === constituents.length
    ) {
      setExcludedItems([]);
    }
  }, [selectedItems]);

  const onSelectAll = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = e;
      if (!target.checked) {
        setSelectedItems([]);
        setExcludedItems([]);
      } else {
        setSelectedItems(
          constituents.map(({ constituentId }) => constituentId),
        );
      }
    },
    [selectedItems, constituents],
  );

  const onSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = e;
      const newSelections = target.checked
        ? [...selectedItems, target.value]
        : selectedItems.filter((id) => id !== target.value);
      const newExclusions = target.checked
        ? excludedItems.filter((id) => id !== target.value)
        : [...excludedItems, target.value];

      setSelectedItems(newSelections);
      setExcludedItems(newExclusions);
    },
    [selectedItems, excludedItems],
  );

  const selectAllChecked = selectedItems.length === constituents.length;

  const onMenuClick = (
    e: React.MouseEvent,
    constituent: SearchConstituent,
    type: PopoverMenuType,
  ) => {
    setActiveMenu({
      target: e.target,
      constituent,
      type,
    });
  };

  const columnConfig = React.useMemo(() => {
    const result: DataListColumn<SearchConstituent>[] = [
      // Checkbox column
      {
        TableHeaderCellProps: {
          size: 'small',
          padding: 'checkbox',
          className: 'md:sticky left-0 z-10',
        },
        TableCellProps: {
          className:
            'absolute -left-2 top-0.5 mt-px w-auto md:sticky md:left-0 md:top-auto',
          size: 'small',
          padding: isMobile ? 'none' : 'checkbox',
        },
        columnKey: 'constituentSelect',
        headerName: !isMobile && (
          <Tooltip
            arrow
            classes={{
              arrow: 'text-text-primary opacity-80',
              tooltip: 'bg-text-primary opacity-80 rounded-sm m-0',
            }}
            enterDelay={500}
            leaveDelay={200}
            placement="top"
            title={translate('SELECT_ALL')}
          >
            <Checkbox onChange={onSelectAll} checked={selectAllChecked} />
          </Tooltip>
        ),
        render: (constituent: SearchConstituent) => (
          <Checkbox
            checked={selectedItems.includes(constituent.constituentId)}
            onChange={onSelect}
            value={constituent.constituentId}
          />
        ),
      },
      // Display name column
      {
        TableHeaderCellProps: { className: 'md:sticky left-[52px] z-10' },
        TableCellProps: {
          className:
            'max-w-[calc(100%-48px)] flex-shrink md:max-w-none md:sticky left-[52px] z-10',
        },
        columnKey: 'displayName',
        headerName: isMobile ? null : 'NAME',
        render: (constituent: SearchConstituent) => (
          <ConstituentAvatar constituent={constituent} hideAvatar />
        ),
      },

      // Standard field columns
      ...ORDERED_COLUMNS.reduce<DataListColumn<SearchConstituent>[]>(
        (arr, col) => {
          // If it is not mobile, use the columns config from the QueryProvider
          if (!isMobile && !columns.includes(col)) {
            return arr;
          }

          // If it is not mobile, we should expect the default columns to exist
          if (isMobile && !FilterParams.columns.defaultValue.includes(col)) {
            return arr;
          }

          switch (col) {
            case ConstituentSearchColumn.BIO:
              return [...arr, bioColumn];
            case ConstituentSearchColumn.INSTAGRAM:
            case ConstituentSearchColumn.TWITTER:
            case ConstituentSearchColumn.LINKEDIN:
            case ConstituentSearchColumn.FACEBOOK:
            case ConstituentSearchColumn.YOUTUBE:
            case ConstituentSearchColumn.TIKTOK:
              if (
                arr.find(
                  ({ columnKey }) => columnKey === socialMediaColumn.columnKey,
                )
              ) {
                return arr;
              }

              return [...arr, socialMediaColumn];
            case ConstituentSearchColumn.LABELS:
              return [...arr, labelsColumn];
            case ConstituentSearchColumn.CONSTITUENT_CATEGORIES:
            case ConstituentSearchColumn.CONSTITUENT_RULES:
              if (arr.find(({ columnKey }) => columnKey === 'categories')) {
                return arr;
              }
              return [
                ...arr,
                {
                  columnKey: 'categories',
                  headerName: isMobile ? null : 'CATEGORIES',
                  TableCellProps: {
                    className: ColumnMaxMinWidthClasses,
                  },
                  render: (constituent: SearchConstituent) => (
                    <ConstituentIconTiles
                      constituent={constituent}
                      size={
                        isMobile
                          ? AttributeIconSize.SMALL
                          : AttributeIconSize.MEDIUM
                      }
                    />
                  ),
                },
              ];
            case ConstituentSearchColumn.DEVELOPERS:
              return [...arr, developersColumn];
            case ConstituentSearchColumn.LAST_INTERACTION_DATE:
              return [...arr, lastInteractionDateColumn];
            case ConstituentSearchColumn.DATE_OF_BIRTH:
              return [...arr, dobColumn];
            case ConstituentSearchColumn.PRIMARY_CITY:
              return [...arr, cityColumn];
            case ConstituentSearchColumn.PRIMARY_STATE:
              return [...arr, stateColumn];
            case ConstituentSearchColumn.PRIMARY_COUNTRY:
              return [...arr, countryColumn];
            case ConstituentSearchColumn.LIFETIME_COUNT:
              return [...arr, lifetimeCountColumn];
            case ConstituentSearchColumn.THIS_YEAR_COUNT:
              return [...arr, thisYearCountColumn];
            case ConstituentSearchColumn.LIFETIME_DONATIONS:
              return [...arr, lifetimeDonationsColumn];
            case ConstituentSearchColumn.THIS_YEAR_DONATIONS:
              return [...arr, thisYearDonationsColumn];
            case ConstituentSearchColumn.LIFETIME_HOURS:
              return [...arr, lifetimeHoursColumn];
            case ConstituentSearchColumn.THIS_YEAR_HOURS:
              return [...arr, thisYearHoursColumn];
            case ConstituentSearchColumn.NUM_CONNECTIONS:
              return [...arr, connectionsColumn];
            default:
              return arr;
          }
        },
        [],
      ),
      // Custom fields
      ...customFieldColumns.map((id) => {
        const field = customFieldData.find(
          ({ customFieldId }) => id === customFieldId,
        );

        return {
          columnKey: id,
          headerName: <span>{field?.fieldName}</span>,
          TableCellProps: {
            className: ColumnMaxMinWidthClasses,
          },
          render: (constituent: SearchConstituent) => {
            const customField = constituent?.customFields?.find(
              ({ customFieldId }) => customFieldId === id,
            );

            if (!customField || !field) {
              return null;
            }

            return renderCustomFieldValue(customField.value, field.fieldType);
          },
        };
      }),
    ];
    // Phone/email action menu
    if (
      columns.includes(ConstituentSearchColumn.PRIMARY_PHONE) ||
      columns.includes(ConstituentSearchColumn.PRIMARY_EMAIL)
    ) {
      result.push({
        columnKey: 'actions',
        headerName: isMobile ? null : 'ACTIONS',
        TableCellProps: {
          className: ColumnMaxMinWidthClasses,
        },
        render: (constituent: SearchConstituent) =>
          constituent.emails?.length ||
          constituent.primaryEmail ||
          constituent.phones?.length ? (
            <ConstituentActions
              className="my-2 md:my-0"
              constituent={constituent}
              onMenuClick={onMenuClick}
              fullWidth={isMobile}
            />
          ) : null,
      });
    }

    // Context menu
    result.push({
      TableCellProps: {
        padding: 'none',
        className: 'absolute -right-3 top-0 w-auto md:static',
      },
      columnKey: 'contextMenu',
      render: (constituent: SearchConstituent) => (
        <IconButton
          onClick={(e) => onMenuClick(e, constituent, PopoverMenuType.OTHER)}
          className="text-text-secondary"
          size="large"
        >
          <MoreHoriz />
        </IconButton>
      ),
    });
    return result;
  }, [
    columns,
    customFieldColumns,
    customFieldData,
    isMobile,
    onMenuClick,
    onSelect,
    onSelectAll,
    selectAllChecked,
    selectedItems,
  ]);

  return (
    <>
      <DataList<SearchConstituent>
        TableRowProps={TABLE_ROW_PROPS}
        TableContainerProps={{ className: 'relative' }}
        columns={columnConfig}
        data={constituents}
        isLagging={isLagging}
        rowKey="constituentId"
        totalRows={totalRows ?? 0}
      />
      <ConstituentPopover
        constituent={activeMenu?.constituent}
        target={activeMenu?.target}
        onClose={() => setActiveMenu(null)}
        open={!!activeMenu}
        type={activeMenu?.type}
      />
    </>
  );
}

export default ConstituentDataList;
